/**
 * iter549 — Dashboard widget registry
 *
 * Single source of truth за всички customizable widgets на Dashboard.
 * Всеки widget има:
 *   id          — unique slug, never changes (стои в backend prefs)
 *   label_bg    — какво вижда потребителят в drawer-а (на български)
 *   label_en    — английски етикет
 *   description_bg / description_en — кратко обяснение защо/каква полза
 *   default_visible — true по подразбиране, false ако е по избор (off by default)
 *   default_order — началната позиция (по-малко = по-горе)
 *   scope       — "daily" | "right" | "body" | "nutrition" | "mobile"
 *   icon        — Lucide icon име (string)
 *
 * NB: Премахването на widget от регистъра НЕ премахва preference-а
 * запазен от backend-а — той просто бива игнориран при render. Това
 * гарантира forward-compatibility ако някой widget се преименува.
 */
import { moduleTitle } from "@/lib/twinCatalog";

export const DASHBOARD_WIDGETS = [
  // === Daily (Дневен) — главен home tab ===
  {
    id: "trial-dashboard",
    label_bg: "Пробен период и абонамент",
    label_en: "Trial / Plan banner",
    description_bg: "Показва оставащите дни от безплатния период или активния план.",
    description_en: "Shows remaining trial days or active plan.",
    default_visible: true,
    default_order: 5,
    scope: "daily",
    icon: "Crown",
  },
  {
    id: "health-hub",
    label_bg: "Здравен център — кратък изглед",
    label_en: "Health Hub overview",
    description_bg: "Премиум preview на 9-те Health OS секции: фенотип, тренировка, ум, метаболизъм. Стартовата точка на твоето здраве.",
    description_en: "Premium overview of all 9 Health OS sections.",
    default_visible: true,
    default_order: 10,
    scope: "daily",
    icon: "LayoutDashboard",
  },
  {
    id: "next-step-card",
    label_bg: "Следваща стъпка",
    label_en: "Next step",
    description_bg: "AI-подбрана единствена най-важна стъпка за днес. Идеално, ако не знаеш откъде да започнеш.",
    description_en: "AI-picked single most-important action for today.",
    default_visible: true,
    default_order: 20,
    scope: "daily",
    icon: "ArrowRight",
  },
  {
    id: "vegan-challenge",
    label_bg: "Веган уикенд предизвикателство",
    label_en: "Vegan weekend challenge",
    description_bg: "Двудневно растително предизвикателство — намалява възпалението и обновява микробиома.",
    description_en: "2-day plant-based challenge to reduce inflammation.",
    default_visible: true,
    default_order: 30,
    scope: "daily",
    icon: "Leaf",
  },
  {
    id: "backfill-nudge",
    label_bg: "Напомняне за неотчетени дни",
    label_en: "Backfill nudge",
    description_bg: "Бутон за бързо попълване на пропуснат хранителен дневник в последните 7 дни.",
    description_en: "Quick-fill button for skipped meal log days.",
    default_visible: true,
    default_order: 40,
    scope: "daily",
    icon: "CalendarClock",
  },

  // === Right panel ===
  {
    id: "daily-insight",
    label_bg: "Днешен извод",
    label_en: "Today's insight",
    description_bg: "AI прозрение на деня — какво е най-важно за теб точно сега, базирано на твоите данни.",
    description_en: "AI insight of the day based on your data.",
    default_visible: true,
    default_order: 100,
    scope: "right",
    icon: "Sparkles",
  },
  {
    id: "smart-notifications",
    label_bg: "Циркадни напомняния",
    label_en: "Circadian notifications",
    description_bg: "Кратки полезни напомняния синхронизирани с биологичния ти часовник (вода, светлина, сън).",
    description_en: "Short tips synced to your biological clock.",
    default_visible: true,
    default_order: 110,
    scope: "right",
    icon: "Bell",
  },
  {
    id: "demo-examples",
    label_bg: "Примерни въпроси за AI",
    label_en: "Demo AI prompts",
    description_bg: "Готови примерни питания към AI коуча — кликни и получи отговор.",
    description_en: "Ready-to-use AI coach prompts.",
    default_visible: true,
    default_order: 120,
    scope: "right",
    icon: "MessageSquareQuote",
  },
  {
    id: "chat-interface",
    label_bg: "Питай AI коуча",
    label_en: "Ask AI coach",
    description_bg: "Чат с твоя личен AI коуч — задай въпрос за хранене, метаболизъм, симптом.",
    description_en: "Chat with your personal AI nutrition coach.",
    default_visible: true,
    default_order: 130,
    scope: "right",
    icon: "MessageCircle",
  },
  {
    id: "dashboard-right-panel",
    label_bg: "Седмично резюме + План + PhenoAge",
    label_en: "Weekly recap + Plan + PhenoAge",
    description_bg: "Седмично резюме, активен план, биологична възраст и upgrade оферти.",
    description_en: "Weekly debrief, active plan, biological age.",
    default_visible: true,
    default_order: 140,
    scope: "right",
    icon: "Activity",
  },
  {
    id: "profile-completeness",
    label_bg: "Готовност на профила",
    label_en: "Profile completeness",
    description_bg: "Колко % от профила ти е попълнен — по-високо = по-точни препоръки.",
    description_en: "How complete your profile is.",
    default_visible: true,
    default_order: 150,
    scope: "right",
    icon: "UserCheck",
  },
  {
    id: "onboarding-checklist",
    label_bg: "Списък за стартиране",
    label_en: "Onboarding checklist",
    description_bg: "Стъпка-по-стъпка какво да направиш в първите дни в Nutramilo.",
    description_en: "Step-by-step onboarding guide.",
    default_visible: true,
    default_order: 160,
    scope: "right",
    icon: "ListChecks",
  },
  {
    id: "locked-value-card",
    label_bg: "Заключени възможности",
    label_en: "Locked premium features",
    description_bg: "Какво ще получиш с по-висок план — бъди винаги наясно.",
    description_en: "Preview of premium features.",
    default_visible: true,
    default_order: 170,
    scope: "right",
    icon: "Lock",
  },
  {
    id: "smart-upgrade",
    label_bg: "Предложение за по-висок план",
    label_en: "Smart upgrade suggestion",
    description_bg: "Персонализирана оферта базирана на твоето използване.",
    description_en: "Personalized upgrade offer.",
    default_visible: false,
    default_order: 180,
    scope: "right",
    icon: "TrendingUp",
  },
  {
    id: "chat-history-list",
    label_bg: "История на AI разговорите",
    label_en: "AI chat history",
    description_bg: "Списък с последните ти AI питания — кликни за бърз достъп.",
    description_en: "Recent AI chat history.",
    default_visible: true,
    default_order: 190,
    scope: "right",
    icon: "History",
  },

  // === Body tab ===
  {
    id: "health-indices",
    label_bg: "Здравни показатели (BMI, HOMA-IR, …)",
    label_en: "Health indices",
    description_bg: "Всички клинични индекси на едно място — BMI, HOMA-IR, eGFR, FINDRISC, CVD риск.",
    description_en: "All clinical indices in one place.",
    default_visible: true,
    default_order: 200,
    scope: "body",
    icon: "Gauge",
  },
  {
    id: "metabolic-panel",
    label_bg: "Метаболитен панел",
    label_en: "Metabolic panel",
    description_bg: "DIL, NIS, UIS, Satiety — твоят дневен метаболитен профил от храненията.",
    description_en: "DIL, NIS, UIS, Satiety — daily metabolic profile.",
    default_visible: true,
    default_order: 210,
    scope: "body",
    icon: "Flame",
  },
  {
    id: "health-calculators-results",
    label_bg: "Резултати от калкулаторите",
    label_en: "Calculator results",
    description_bg: "Запазените стойности от всички калкулатори (BMI, HOMA-IR, eGFR и др.).",
    description_en: "Saved values from all calculators.",
    default_visible: true,
    default_order: 220,
    scope: "body",
    icon: "Calculator",
  },
  {
    id: "dental-risk",
    label_bg: "Дентален риск",
    label_en: "Dental risk",
    description_bg: "Преглед на устния ти риск — кариес, пародонтит, микробиом. Кликни за пълна оценка.",
    description_en: "Oral risk overview — caries, periodontal, microbiome.",
    default_visible: true,
    default_order: 230,
    scope: "body",
    icon: "Smile",
  },

  {
    id: "cortisol-intel",
    label_bg: moduleTitle("cortisol"),
    label_en: "Cortisol intelligence",
    description_bg: "Профил, ден от 28-дневната програма, днешният напредък и серията.",
    description_en: "Profile, day of the 28-day program, today's progress and streak.",
    default_visible: true,
    default_order: 235,
    scope: "body",
    icon: "Activity",
  },
  {
    id: "khi-intel",
    label_bg: moduleTitle("khi"),
    label_en: "Kidney intelligence",
    description_bg: "Бъбречна оценка, стадий, планът за днес и следващата стъпка.",
    description_en: "Kidney score, stage, today's plan and next step.",
    default_visible: true,
    default_order: 236,
    scope: "body",
    icon: "Droplets",
  },
  {
    id: "bhi-intel",
    label_bg: moduleTitle("bhi"),
    label_en: "Breast intelligence",
    description_bg: "Метаболитна среда, тъканна възраст и водещата интервенция.",
    description_en: "Metabolic environment, tissue age and the top intervention.",
    default_visible: true,
    default_order: 237,
    scope: "body",
    icon: "HeartPulse",
  },
  {
    id: "dhi-intel",
    label_bg: moduleTitle("dhi"),
    label_en: "Dental intelligence",
    description_bg: "Рискова категория, просрочени процедури и отмятане на навиците.",
    description_en: "Risk category, overdue procedures and habit check-ins.",
    default_visible: true,
    default_order: 238,
    scope: "body",
    icon: "Smile",
  },
  {
    id: "neuro-intel",
    label_bg: moduleTitle("neuro"),
    label_en: "Neuro intelligence",
    description_bg: "Невропатия, защитна чувствителност и автономен баланс (симпатикус ↔ вагус).",
    description_en: "Neuropathy, protective sensation and autonomic balance.",
    default_visible: true,
    default_order: 240,
    scope: "body",
    icon: "Activity",
  },
  {
    id: "hema-intel",
    label_bg: moduleTitle("hema"),
    label_en: "Hematology intelligence",
    description_bg: "Анемичен модел, железен статус и връзките към бъбрека, възпалението и метаболизма.",
    description_en: "Anemia pattern, iron status and links to kidney, inflammation and metabolism.",
    default_visible: true,
    default_order: 239,
    scope: "body",
    icon: "Droplet",
  },


  // === Hero / banner widgets (iter553) ===
  {
    id: "metabolic-hero",
    label_bg: "Метаболитен поздрав и лента за DIL",
    label_en: "Metabolic greeting + DIL banner",
    description_bg: `Поздрав в зависимост от часа на деня + дневен DIL/калории банер с бутон „Бърз запис".`,
    description_en: "Time-of-day greeting + daily DIL/kcal banner with quick-log button.",
    default_visible: true,
    default_order: 1,
    scope: "daily",
    icon: "Sun",
  },

  // === Nutrition tab (iter553) ===
  {
    id: "food-image-analyzer",
    label_bg: "AI анализ на снимка на храна",
    label_en: "AI food photo analyzer",
    description_bg: "Снимай храна и AI разпознава съставките + дава DIL, калории и инсулинов отговор.",
    description_en: "Snap food and AI identifies ingredients + computes DIL, calories, insulin response.",
    default_visible: true,
    default_order: 400,
    scope: "nutrition",
    icon: "Camera",
  },
  {
    id: "meal-history-panel",
    label_bg: "История на храненията",
    label_en: "Meal history",
    description_bg: "Бърз достъп до последните ти хранения с опция за копиране и повторно записване.",
    description_en: "Quick access to recent meals with copy & re-log.",
    default_visible: true,
    default_order: 410,
    scope: "nutrition",
    icon: "History",
  },

  // === Analyses tab (iter553) ===
  {
    id: "vais-card",
    label_bg: "VAIS — показател за висцералната мазнина",
    label_en: "VAIS — Visceral adiposity",
    description_bg: "Сборна оценка на мазнините около вътрешните органи. Висцералната мазнина е по-опасна от подкожната.",
    description_en: "Composite score of fat around internal organs.",
    default_visible: true,
    default_order: 500,
    scope: "analyses",
    icon: "Scale",
  },
  {
    id: "vais-reduction-pathway",
    label_bg: "VAIS — План за намаление",
    label_en: "VAIS — Reduction pathway",
    description_bg: "Стъпки за намаляване на висцералната мазнина: хранене, движение, сън, стрес.",
    description_en: "Steps to reduce visceral fat: diet, movement, sleep, stress.",
    default_visible: true,
    default_order: 510,
    scope: "analyses",
    icon: "TrendingDown",
  },

  // === Diary tab — Analysis section (iter553) ===
  {
    id: "weekly-il-trend",
    label_bg: "Седмичен инсулинов товар",
    label_en: "Weekly insulin load trend",
    description_bg: "Графика на дневния DIL за последните 7-14 дни. Виж посоката на прогреса си.",
    description_en: "Daily DIL chart for the last 7-14 days.",
    default_visible: true,
    default_order: 600,
    scope: "diary",
    icon: "LineChart",
  },
  {
    id: "dietary-patterns",
    label_bg: "Хранителни модели",
    label_en: "Dietary patterns",
    description_bg: "Анализ на твоите хранителни навици: средиземноморски, плант-базиран, кето и т.н.",
    description_en: "Analysis of your eating patterns.",
    default_visible: true,
    default_order: 610,
    scope: "diary",
    icon: "Pizza",
  },
  {
    id: "nutrient-deficiency-diary",
    label_bg: "Недостиг на хранителни вещества (дневник)",
    label_en: "Deficiencies (Diary)",
    description_bg: "11 ключови нутриента, които може да са под препоръчителния минимум. С AI коментар.",
    description_en: "Top 11 nutrients potentially below RDI.",
    default_visible: true,
    default_order: 620,
    scope: "diary",
    icon: "AlertCircle",
  },
  {
    id: "weekly-chart",
    label_bg: "Седмичен преглед (графики)",
    label_en: "Weekly review (charts)",
    description_bg: "Sparkline графики на DIL, калории, фибри, протеин за последните 7 дни.",
    description_en: "Sparkline charts of DIL, calories, fiber, protein.",
    default_visible: true,
    default_order: 630,
    scope: "diary",
    icon: "BarChart3",
  },
  {
    id: "col3-daily-tracker",
    label_bg: "Col3 — дневен отчет",
    label_en: "Col3 daily tracker",
    description_bg: "Проследяване на синтеза на колаген — витамин C, мед, лизин, пролин, цинк.",
    description_en: "Collagen synthesis tracking — vitamin C, copper, lysine, proline, zinc.",
    default_visible: false,
    default_order: 640,
    scope: "diary",
    icon: "Sparkles",
  },
  {
    id: "col3-foods-catalogue",
    label_bg: "Col3 — Каталог храни по нутриент",
    label_en: "Col3 foods by nutrient",
    description_bg: "Списък с най-добрите храни за всеки нутриент необходим за синтез на колаген.",
    description_en: "Best foods for each collagen-synthesis nutrient.",
    default_visible: false,
    default_order: 650,
    scope: "diary",
    icon: "Book",
  },

  // === Diary AI section (iter553) ===
  {
    id: "meal-plan-review",
    label_bg: "Преглед на хранителен режим",
    label_en: "Meal plan review",
    description_bg: "AI преглед и оценка на твоя план за хранене — какво да оптимизираш.",
    description_en: "AI review of your meal plan.",
    default_visible: true,
    default_order: 660,
    scope: "diary",
    icon: "ClipboardCheck",
  },
  {
    id: "smart-stack-widget",
    label_bg: "Подбран набор от добавки",
    label_en: "Smart stack",
    description_bg: "Препоръчителни хранителни добавки според твоите дефицити и цели.",
    description_en: "Recommended supplements based on your gaps.",
    default_visible: true,
    default_order: 670,
    scope: "diary",
    icon: "Pill",
  },

  // === Individual Health Indices (iter552 — granular toggling) ===
  {
    id: "idx-bmi",
    label_bg: "BMI — показател за телесна маса",
    label_en: "BMI — Body Mass Index",
    description_bg: "Съотношение тегло/ръст². Бърза проверка дали си в здравословен диапазон за твоя ръст.",
    description_en: "Weight/height² ratio — fast check if you're in a healthy range for your height.",
    default_visible: true,
    default_order: 300,
    scope: "indices",
    icon: "Scale",
  },
  {
    id: "idx-whr",
    label_bg: "WHR — Мазнини около кръста",
    label_en: "WHR — Belly fat ratio",
    description_bg: "Талия/ханш. Мазнините на корема влияят повече на сърцето от тези по бедрата.",
    description_en: "Waist/hip ratio. Belly fat affects the heart more than hip fat.",
    default_visible: true,
    default_order: 310,
    scope: "indices",
    icon: "Ruler",
  },
  {
    id: "idx-whtr",
    label_bg: "WHtR — Талия спрямо ръст",
    label_en: "WHtR — Waist-to-height",
    description_bg: "По-точен от BMI за висцерален риск. Под 0.53 = безопасно.",
    description_en: "More accurate than BMI for visceral risk. Below 0.53 = safe.",
    default_visible: true,
    default_order: 320,
    scope: "indices",
    icon: "Ruler",
  },
  {
    id: "idx-homa-ir",
    label_bg: "HOMA-IR — Инсулинова чувствителност",
    label_en: "HOMA-IR — Insulin sensitivity",
    description_bg: "Колко добре тялото ти използва инсулина. По-ниска стойност = по-добра чувствителност.",
    description_en: "How well your body uses insulin. Lower = better.",
    default_visible: true,
    default_order: 330,
    scope: "indices",
    icon: "Activity",
  },
  {
    id: "idx-body-fat",
    label_bg: "Телесни мазнини %",
    label_en: "Body fat %",
    description_bg: "Приблизителен процент мазнини, изчислен от BMI, възраст и пол.",
    description_en: "Estimated body fat %, calculated from BMI, age, gender.",
    default_visible: true,
    default_order: 340,
    scope: "indices",
    icon: "Flame",
  },
  {
    id: "idx-dil-budget",
    label_bg: "Дневен инсулинов бюджет (DIL)",
    label_en: "Daily insulin budget (DIL)",
    description_bg: `Колко „инсулин-стимулираща" храна е подходяща за теб за един ден. Личен бюджет.`,
    description_en: "Personal daily DIL budget based on your metabolic profile.",
    default_visible: true,
    default_order: 350,
    scope: "indices",
    icon: "Zap",
  },
];

/**
 * Build the effective widget list for a given scope, respecting user prefs.
 * Returns widgets in user-defined order, with hidden ones excluded.
 */
export function resolveWidgets(scope, layout) {
  const order = Array.isArray(layout?.order) ? layout.order : [];
  const hidden = new Set(Array.isArray(layout?.hidden) ? layout.hidden : []);
  const all = DASHBOARD_WIDGETS.filter((w) => w.scope === scope);
  const byId = Object.fromEntries(all.map((w) => [w.id, w]));

  // First pass: those listed in user order (and known)
  const ordered = [];
  const seen = new Set();
  for (const id of order) {
    if (byId[id] && !seen.has(id)) {
      ordered.push(byId[id]);
      seen.add(id);
    }
  }
  // Append remaining (sorted by default_order)
  const remaining = all
    .filter((w) => !seen.has(w.id))
    .sort((a, b) => a.default_order - b.default_order);

  return [...ordered, ...remaining].filter((w) => !hidden.has(w.id));
}

/** Whether a widget is visible given user preferences. */
export function isWidgetVisible(id, layout) {
  if (!layout) return true;
  const hidden = Array.isArray(layout.hidden) ? layout.hidden : [];
  // unknown widget id → fallback to its registry default
  const meta = DASHBOARD_WIDGETS.find((w) => w.id === id);
  if (!meta) return true;
  if (hidden.includes(id)) return false;
  // If user has explicit order, the widget exists in user pref → visible
  return true;
}
